/**
 * How long to wait for a Blob's bytes before treating the read as stalled.
 *
 * Matches SNAPSHOT_READ_TIMEOUT_MS in order/new/utils/file-snapshot.ts: both
 * bound the same underlying hazard, an iOS photo that lives in iCloud rather
 * than on the device. A read that has to come down from iCloud can take minutes
 * or never finish; a read from local storage takes milliseconds.
 */
export const FILE_READ_TIMEOUT_MS = 20_000;

/**
 * Reads a Blob's bytes, giving up after `timeoutMs` instead of waiting forever.
 *
 * `blob.arrayBuffer()` has no timeout of its own. In the thumbnail fallback
 * that read is awaited inside `processBatch`, which gates the uploader's
 * spinner and its disabled submit button — so a single photo whose bytes never
 * arrive froze the picker indefinitely, with no error shown and no telemetry
 * sent. Two customers lost days to exactly that.
 *
 * Rejects with a distinguishable timeout error so callers can tell "still
 * downloading from iCloud" apart from "this file is gone"; a genuine read
 * failure propagates unchanged.
 */
export function readArrayBufferWithTimeout(
  blob: Blob,
  timeoutMs: number = FILE_READ_TIMEOUT_MS,
): Promise<ArrayBuffer> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Reading file bytes timed out after ${timeoutMs}ms`)),
      timeoutMs,
    );
  });

  return Promise.race([blob.arrayBuffer(), timedOut]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}
